import { errorFromResponse, notifications } from '~/app/notifications'
import AppButton from '@theme/components/atom/AppButton'
import AppInput from '@theme/components/atom/AppInput'
import AppTextarea from '@theme/components/atom/AppTextarea'
import AppTitle from '@theme/components/atom/AppTitle'
import AresLoading from '@theme/components/cart/AresLoading'
import Breadcrumbs from '@theme/components/molecule/Breadcrumbs'
import CheckoutCountrySelect from '@theme/components/cart/CheckoutCountrySelect'
import ContactBox from '@theme/components/shop/ContactBox'
import FoxentryFormMixin from '~/mixins/FoxentryFormMixin'
import LayoutBase from '@theme/components/shop/LayoutBase'
import LoadingMixin from '~/mixins/LoadingMixin'
import PageRegisterContentAboveRegisterForm from '@theme/components/user/PageRegisterContentAboveRegisterForm'
import PhoneInput from '@theme/components/molecule/PhoneInput'
import ProvideValidator from '~/app/ProvideValidator'
import RegistrationBenefits from '@theme/components/content/RegistrationBenefits'
import RequiredSign from '@theme/components/atom/RequiredSign'
import SeoMixin from '~/mixins/SeoMixin'
import TooltipOpener from '@theme/components/molecule/TooltipOpener'
import TypoRecognition from '@theme/components/utils/TypoRecognition'
import UserMixin from '~/mixins/UserMixin'
import _ from 'lodash'

export default {
  components: {
    AppButton,
    AppInput,
    AppTextarea,
    AppTitle,
    AresLoading,
    Breadcrumbs,
    CheckoutCountrySelect,
    ContactBox,
    LayoutBase,
    PageRegisterContentAboveRegisterForm,
    PhoneInput,
    RegistrationBenefits,
    RequiredSign,
    TooltipOpener,
    TypoRecognition,
  },
  mixins: [FoxentryFormMixin, LoadingMixin, ProvideValidator, SeoMixin, UserMixin],
  head() {
    return this.getSeoHead({ yamlKey: 'PageRegister', noindex: true, nofollow: false })
  },
  data() {
    return {
      breadcrumbs: [
        {
          title: this.$t('PageRegister.breadcrumbs.index'),
          path: this.localePath({ name: 'register' }),
        },
      ],
      formalTitles: this.$store.state.globals.forms.formalTitles,
      input: {
        businessType: this.$themeSettings.components.PageRegister.enableBusinessTypeOption
          ? this.$store.state.globals.forms.businessTypes[0]
          : null,
        formalTitle: '',
        profession: '',
        fullName: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        passwordConfirmation: '',
        street: '',
        houseNumber: '',
        city: '',
        postcode: '',
        country: null,
        businessName: '',
        tin: '',
        vatin: '',
        vatinsvk: '',
        note: '',
        privacyPolicy: false,
        complianceForEmailMarketing: this.$themeSettings.components.PageRegister.newsletter.defaultValue,
        complianceForSmsMarketing: false,
      },
      isBusiness: false,
      registrationSuccess: false,
      slugPrivacyPolicy: this.$t('globals.pageSlugs.privacyPolicy'),
      slugPrivacyPolicyMarketing: this.$t('globals.pageSlugs.privacyPolicyMarketing'),
    }
  },
  created() {
    this.CLASS_ELEMENT = 'col-sm-16 col-lg-14 col-xl-12'
    this.CLASS_ELEMENTS_OUTSIDE_NO_LABEL = 'offset-md-8 offset-lg-6 col'
    this.CLASS_INPUT_TEXT = ''
    this.CLASS_LABEL = 'col-form-label col-sm-8 col-lg-6 mb-0'
    this.CLASS_ROW = 'form-group row'
    this.CLASS_ROW_CHECKBOX = 'form-group checkbox-custom'
    const passwordConfirmValidation = {
      getMessage: (field, args) => {
        return this.$t('PageRegister.notifications.passwordsDontMatch')
      },
      validate: (value, args) => {
        return this.input.passwordConfirmation === this.input.password
      },
    }
    this.$validator.extend('password_confirmation', passwordConfirmValidation, {
      immediate: false,
    })
    if (this.$store.state.globals.countries.length === 1) {
      this.input.country = this.$store.state.globals.countries[0].code
    }
    // If config mode is enabled
    if (this.$store.getters['foxentry/GET_IS_CONFIG_MODE']) {
      this.isBusiness = true
    }
  },
  computed: {
    businessTypes() {
      if (
        this.$themeSettings.components.PageRegister.enableBusinessTypeOption &&
        this.$store.state.globals.forms.businessTypes &&
        this.$store.state.globals.forms.businessTypes.length > 0
      ) {
        return this.$store.state.globals.forms.businessTypes
      }
      return []
    },
    displayVatinsvk() {
      const country = this.input.country ? this.input.country : this.$store.state.globals.forms.countryCode
      return country === 'SVK'
    },
    enableVatinInputField() {
      const country = this.input.country ? this.input.country : this.$store.state.globals.forms.countryCode
      return !this.$themeSettings.components.PageRegister.disableVatinInputForCountries.includes(country)
    },
    showProfession() {
      return this.$themeSettings.components.PageRegister.profession.enable
    },
  },
  methods: {
    prepareData() {
      const data = {
        name: this.$themeSettings.global.userFullName
          ? this.input.fullName
          : this.input.firstName + ' ' + this.input.lastName,
        firstName: this.$themeSettings.global.userFullName
          ? this.getUserFirstName(this.input.fullName)
          : this.input.firstName,
        lastName: this.$themeSettings.global.userFullName
          ? this.getUserLastName(this.input.fullName)
          : this.input.lastName,
        email: this.input.email,
        phone: this.input.phone,
        password: this.input.password,
        password_confirmation: this.input.passwordConfirmation,
        newsletter: this.input.complianceForEmailMarketing,
        phone_notification: this.input.complianceForSmsMarketing,
        address: {
          street: this.input.houseNumber ? `${this.input.street} ${this.input.houseNumber}` : this.input.street,
          city: this.input.city,
          postcode: this.input.postcode,
          country_code: this.input.country,
        },
        terms: this.input.privacyPolicy,
        custom: {},
        callbackUrl: window.location.origin + this.localePath({ name: 'user-activate' }),
      }
      if (this.isBusiness) {
        data.business = {}
        data.business.business_name = this.input.businessName
        data.business.TIN = this.input.tin
        data.business.VATIN = this.input.vatin
        data.custom.ICDPH = this.input.vatinsvk
      }
      if (this.input.businessType !== null) {
        data.custom.business = this.input.businessType === 'b2b'
      }
      if (this.input.formalTitle) {
        data.custom.formalTitle = this.input.formalTitle
      }
      if (this.input.note) {
        data.custom.note = this.input.note
      }
      if (this.showProfession) {
        data.custom.profession = this.input.profession
      }
      return data
    },
    async submit() {
      if (!(await this.foxentryFormSubmit('#PageRegisterForm'))) {
        return
      }
      if (!(await this.$validator.validate())) {
        notifications.danger(this.$validator.errors.items[0].msg)
        return
      }
      this.startLoading()
      const response = await this.$store.dispatch('user/REGISTER', this.prepareData())
      if (response.status) {
        const backendFieldsMapping = {
          name: 'fullName',
          firstName: 'firstName',
          lastName: 'lastName',
          email: 'email',
          phone: 'phone',
          newsletter: 'complianceForEmailMarketing',
          'address.street': 'street',
          'address.city': 'city',
          'address.postcode': 'postcode',
          'address.country_code': 'country',
          business: 'businessName',
          password: 'password',
          password_confirmation: 'passwordConfirmation',
          terms: 'terms',
        }
        _.forOwn(response.data.errors, error => {
          if (backendFieldsMapping[error.field]) {
            this.errors.add({
              field: backendFieldsMapping[error.field],
              msg: error.title,
            })
          }
        })
        if (response.data.errors && response.data.errors[0].field !== 'terms') {
          if (response.status === 401 && response.data.errors && response.data.errors[0]) {
            // For the case when e-mail validation is needed
            notifications.success(response.data.errors[0].title)
            this.success()
          } else {
            errorFromResponse(response)
          }
        } else {
          notifications.danger(this.$t('PageRegister.notifications.gdprConsentRequired'))
        }
      } else {
        this.success()
      }
      this.stopLoading()
    },
    success() {
      this.registrationSuccess = true
      this.$scrollTo(window.document.body)
    },
    toggleBillingInfo() {
      this.isBusiness = !this.isBusiness
    },
    firstFilled(...values) {
      for (const value of values) {
        if (value !== null && value !== undefined && `${value}`.trim() !== '') {
          return value
        }
      }
      return null
    },
    updateUser(value) {
      const business = value && value.business ? value.business : {}
      const address = value && value.address ? value.address : {}
      const mappedValues = {
        street: this.firstFilled(address.street, value && value.street),
        city: this.firstFilled(address.city, value && value.city),
        country: this.firstFilled(address.countryCode, address.country_code, value && value.countryCode),
        postcode: this.firstFilled(address.postcode, value && value.postcode),
        businessName: this.firstFilled(
          business.businessName,
          business.business_name,
          business.companyName,
          business.name,
          value && value.businessName,
          value && value.business_name,
          value && value.companyName,
          value && value.obchodniJmeno,
          value && value.name,
        ),
        vatin: this.firstFilled(business.vatin, business.VATIN, value && value.vatin, value && value.VATIN),
      }

      Object.entries(mappedValues).forEach(([field, fieldValue]) => {
        if (fieldValue !== null) {
          this.input[field] = fieldValue
        }
      })
    },
  },
  watch: {
    'input.businessType': function(newValue) {
      if (this.$themeSettings.components.PageRegister.enableToggleBusinessInputsOnB2bTypeSelection) {
        if (newValue === 'b2b') {
          this.isBusiness = true
          return
        }
        this.isBusiness = false
      }
    },
  },
}
